import type {
  PlatformBroadcastTranslation,
  PlatformBroadcastTranslationAssistResult,
  SupportedLocale,
} from '@accestra/types';

export type EditableBroadcastTranslations = Record<SupportedLocale, PlatformBroadcastTranslation>;

function normalizeText(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim() : '';
}

export function getBroadcastTranslationSourceSignature(
  translation: PlatformBroadcastTranslation,
): string {
  return JSON.stringify({
    title: normalizeText(translation.title),
    body: normalizeText(translation.body),
    ctaLabel: normalizeText(translation.ctaLabel),
  });
}

export function applyBroadcastTranslationAssistResult(input: {
  current: EditableBroadcastTranslations;
  result: PlatformBroadcastTranslationAssistResult;
}): {
  translations: EditableBroadcastTranslations;
  generatedLocales: SupportedLocale[];
} {
  const nextTranslations: EditableBroadcastTranslations = {
    ...input.current,
  };
  const generatedLocales: SupportedLocale[] = [];

  for (const [locale, localeResult] of Object.entries(input.result.locales)) {
    if (!localeResult || !localeResult.translation || localeResult.appliedFields.length === 0) {
      continue;
    }

    const currentLocale = nextTranslations[locale as SupportedLocale];
    const nextLocale: PlatformBroadcastTranslation = {
      ...currentLocale,
    };

    for (const field of localeResult.appliedFields) {
      nextLocale[field] = localeResult.translation[field] ?? '';
    }

    nextTranslations[locale as SupportedLocale] = nextLocale;
    generatedLocales.push(locale as SupportedLocale);
  }

  return {
    translations: nextTranslations,
    generatedLocales,
  };
}

export function isGeneratedBroadcastTranslationLocaleStale(input: {
  english: PlatformBroadcastTranslation;
  sourceSignature: string | null | undefined;
}): boolean {
  if (!input.sourceSignature) {
    return false;
  }

  return getBroadcastTranslationSourceSignature(input.english) !== input.sourceSignature;
}

export function areGeneratedBroadcastTranslationsStale(input: {
  english: PlatformBroadcastTranslation;
  generatedSourceSignatures: Partial<Record<SupportedLocale, string>>;
}): boolean {
  const signatures = Object.values(input.generatedSourceSignatures).filter((value): value is string => Boolean(value));
  if (signatures.length === 0) {
    return false;
  }

  return signatures.some((sourceSignature) => isGeneratedBroadcastTranslationLocaleStale({
    english: input.english,
    sourceSignature,
  }));
}
